/* eslint-disable */

import React, { Component } from "react";
import LocalMachineService from "services/localMachineService";
import LocalService from "services/localService";
import VisitTransactionService from "services/visitTransactionService";
import PropTypes from "prop-types";
import {
  Grid,
  Paper,
  Typography,
  Autocomplete,
  TextField,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import esLocale from "date-fns/locale/es";
import CancelButton from "components/common/CancelButton";
import AcceptButton from "components/common/AcceptButton";
import AppFormContext from "components/common/AppFormContext";
import {
  dateOnlyFromDateTime,
  getDefaultOperationDate,
  isBeforeMinOperationDate,
  isFutureOperationDate,
  isValidOperationDate,
  parseDateOnlyToLocalDate,
  toLocalDateOnlyString,
} from "utils/operationDate";

class DeleteLocalMachineForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      localMachine: props.localMachine || {},
      isDeleting: false,
      depositos: [],
      isLoadingLocals: true,
      selectedDeposito: null,
      effectiveDate: getDefaultOperationDate(),
      lastMachineCollectionDate: null,
      moveDateBounds: null,
      generalError: "",
      showDepositoError: false,
    };
  }

  async componentDidMount() {
    try {
      const localMachineId = this.state.localMachine?.Id;
      const [depositos, lastCollection, moveDateBounds] = await Promise.all([
        LocalService.getActiveLocalDepositoMachines(),
        localMachineId
          ? VisitTransactionService.getLastCollectionByMachineInLocal(localMachineId)
          : Promise.resolve(null),
        localMachineId
          ? LocalMachineService.getMoveDateBounds(localMachineId)
          : Promise.resolve(null),
      ]);

      const lastMachineCollectionDate = parseDateOnlyToLocalDate(
        dateOnlyFromDateTime(lastCollection?.visit?.DateTime)
      );
      const boundsMinDate = parseDateOnlyToLocalDate(moveDateBounds?.MinEffectiveDate);
      const nextEffectiveDate =
        boundsMinDate && isBeforeMinOperationDate(this.state.effectiveDate, boundsMinDate)
          ? boundsMinDate
          : this.state.effectiveDate;

      this.setState({
        depositos: depositos || [],
        isLoadingLocals: false,
        lastMachineCollectionDate,
        moveDateBounds,
        effectiveDate: nextEffectiveDate,
      });
    } catch (error) {
      console.error("Error al cargar locales depósito:", error);
      this.setState({ isLoadingLocals: false });
    }
  }

  handleDateChange = (date) => {
    this.setState({ effectiveDate: date, generalError: "" });
  };

  getMinEffectiveDate = () => {
    const startDate = parseDateOnlyToLocalDate(
      dateOnlyFromDateTime(this.state.localMachine?.StartDateTime)
    );
    const lastMachineCollectionDate = this.state.lastMachineCollectionDate;
    const boundsMinDate = parseDateOnlyToLocalDate(this.state.moveDateBounds?.MinEffectiveDate);

    const dates = [startDate, lastMachineCollectionDate, boundsMinDate].filter(Boolean);
    if (!dates.length) return null;

    return dates.reduce((latest, current) =>
      current.getTime() >= latest.getTime() ? current : latest
    );
  };

  getEffectiveDateHelperText = () => {
    const minDate = this.getMinEffectiveDate();
    if (!minDate) return "";

    const counterConfigText =
      this.state.moveDateBounds?.ReferenceKind === "counter_config"
        ? " No se puede seleccionar una fecha anterior a la última configuración de contadores."
        : "";

    return `Fecha minima permitida: ${minDate.toLocaleDateString("es-UY")}.${counterConfigText}`;
  };

  getEffectiveDateErrorMessage = () => {
    const { effectiveDate, lastMachineCollectionDate, moveDateBounds } = this.state;
    const minDate = this.getMinEffectiveDate();
    const boundsMinDate = parseDateOnlyToLocalDate(moveDateBounds?.MinEffectiveDate);

    if (!isValidOperationDate(effectiveDate)) {
      return "Selecciona una fecha válida.";
    }
    if (isFutureOperationDate(effectiveDate)) {
      return "No se puede seleccionar una fecha futura.";
    }
    if (
      lastMachineCollectionDate &&
      isBeforeMinOperationDate(effectiveDate, lastMachineCollectionDate)
    ) {
      return "La fecha de retiro no puede ser anterior a la última recaudación de esta máquina.";
    }
    if (
      moveDateBounds?.ReferenceKind === "counter_config" &&
      boundsMinDate &&
      isBeforeMinOperationDate(effectiveDate, boundsMinDate)
    ) {
      return "No se puede seleccionar una fecha anterior a la última configuración de contadores.";
    }
    if (minDate && isBeforeMinOperationDate(effectiveDate, minDate)) {
      return "La fecha seleccionada no puede ser anterior al último movimiento de la máquina.";
    }
    return "";
  };

  getApiErrorMessage = (error, fallback) => {
    const status = error?.response?.status;
    const apiError = error?.response?.data?.error || fallback;
    const apiCode = error?.response?.data?.code || "";

    if (status === 409 && apiCode === "EFFECTIVE_DATE_BEFORE_MACHINE_CREATION") {
      return "La fecha seleccionada no puede ser anterior a la fecha de creación de la máquina.";
    }
    if (status === 409 && apiCode === "EFFECTIVE_DATE_BEFORE_CURRENT_LOCAL_MACHINE_START") {
      return "La fecha seleccionada no puede ser anterior al último movimiento de la máquina.";
    }
    if (status === 409 && apiCode === "EFFECTIVE_DATE_BEFORE_LAST_MACHINE_COLLECTION") {
      return "La fecha de retiro no puede ser anterior a la última recaudación de esta máquina.";
    }
    if (status === 409 && apiCode === "LOCAL_MACHINE_FINISH_BEFORE_COUNTER_CONFIG") {
      return "No se puede retirar la máquina con fecha anterior a una configuración de contadores ya registrada.";
    }
    if (status === 400 && apiCode === "EFFECTIVE_DATE_CANNOT_BE_FUTURE") {
      return "No se puede seleccionar una fecha futura.";
    }

    return apiError || fallback;
  };

  handleDelete = async () => {
    const { localMachine, selectedDeposito, effectiveDate } = this.state;

    const localMachineId = localMachine.Id;
    if (!localMachineId) {
      this.setState({ generalError: "No se encontró una LocalMachine válida." });
      return;
    }

    if (!selectedDeposito?.Id) {
      this.setState({ showDepositoError: true, generalError: "" });
      return;
    }

    const effectiveDateError = this.getEffectiveDateErrorMessage();
    if (effectiveDateError) {
      return;
    }

    try {
      this.setState({ isDeleting: true, generalError: "" });

      await LocalMachineService.moveLocalMachine(
        localMachineId,
        selectedDeposito.Id,
        toLocalDateOnlyString(effectiveDate)
      );

      this.props.onDeleteSuccess?.(false);
      this.props.closeForm();
    } catch (error) {
      console.error("Hubo un error al mover la máquina del local:", error);
      this.setState({
        isDeleting: false,
        generalError: this.getApiErrorMessage(
          error,
          "No se pudo retirar la máquina. Intenta nuevamente."
        ),
      });
    }
  };

  render() {
    const {
      isDeleting,
      depositos,
      isLoadingLocals,
      selectedDeposito,
      effectiveDate,
      generalError,
    } = this.state;
    const minEffectiveDate = this.getMinEffectiveDate();
    const effectiveDateError = this.getEffectiveDateErrorMessage();
    const effectiveDateHelperText = this.getEffectiveDateHelperText();
    const today = getDefaultOperationDate();
    const localMachine = this.props.localMachine || {};
    const machine = localMachine.Machine || {};
    const machineType = localMachine?.MachineType?.Type || machine?.MachineType?.Type || "";
    const machineName = machine.Name || localMachine.Name || "Sin nombre";
    const machineSerial = machine.SerialNumber || localMachine.SerialNumber;
    const currentLocalName = localMachine?.Local?.Name || localMachine?.LocalName;

    const getLocalLabel = (loc) =>
      loc?.Name ?? loc?.LocalName ?? `Local #${loc?.Id ?? "?"}`;

    return (
      <Paper style={{ padding: 20, marginTop: 10, width: "100%" }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <AppFormContext
              title={[machineType, machineName].filter(Boolean).join(" ")}
              lines={[
                ...(machineSerial ? [`Serie: ${machineSerial}`] : []),
                ...(currentLocalName ? [`Local actual: ${currentLocalName}`] : []),
              ]}
            />
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              options={depositos}
              getOptionLabel={(option) => getLocalLabel(option)}
              isOptionEqualToValue={(opt, val) => opt.Id === val.Id}
              value={selectedDeposito}
              onChange={(_e, value) => this.setState({ selectedDeposito: value, showDepositoError: false, generalError: "" })}
              disabled={isLoadingLocals}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Depósito destino"
                  required
                  placeholder="Selecciona un depósito"
                  error={this.state.showDepositoError && !selectedDeposito}
                  helperText={this.state.showDepositoError && !selectedDeposito ? "Seleccioná un depósito destino." : ""}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={esLocale}>
              <DatePicker
                label="Fecha de retiro"
                value={effectiveDate}
                onChange={this.handleDateChange}
                minDate={minEffectiveDate || undefined}
                maxDate={today}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    error: Boolean(effectiveDateError),
                    helperText: effectiveDateError || effectiveDateHelperText,
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>

          {generalError && (
            <Grid item xs={12}>
              <Typography variant="body2" color="error">
                {generalError}
              </Typography>
            </Grid>
          )}

          <Grid item xs={12} sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <CancelButton
                  onClick={this.props.closeForm}
                  disabled={isDeleting}
                  fullWidth
                >
                  Cancelar
                </CancelButton>
              </Grid>

              <Grid item xs={12} md={6}>
                <AcceptButton
                  type="button"
                  onClick={this.handleDelete}
                  disabled={isDeleting || Boolean(effectiveDateError)}
                  fullWidth
                >
                  {isDeleting ? "Procesando..." : "Retirar máquina"}
                </AcceptButton>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>
    );
  }
}

DeleteLocalMachineForm.propTypes = {
  closeForm: PropTypes.func.isRequired,
  localMachine: PropTypes.object.isRequired,
  onDeleteSuccess: PropTypes.func.isRequired,
};

export default DeleteLocalMachineForm;
